import type { GA4AnalyticsEvent } from '@/domain/shared/types'
import type { FilterSelection } from '@/scopes/prices/domain/filters/types'
import type { Price } from '@/scopes/prices/domain/prices/types'
import type { RoomInfo } from '@/scopes/prices/domain/rooms/types'
import { FlightType } from '@/scopes/prices/domain/transports/types'
import { PriceTableAnalyticsWithBrand } from '@/scopes/prices/domain/types'
import { formatParticipantDistributionToGA4Result } from '@/scopes/search/search/domain/analytics/mappers'
import { BusInfo, BusType, RouteInfo } from '../transports/types'

export interface PricesFilterClickEventProps {
  element: 'dropdown' | 'participants selector' | 'tab'
  type: string
  result: string | number | readonly string[] | undefined
}

export function createPricesFilterClickEvent({
  element,
  type,
  result,
}: PricesFilterClickEventProps): GA4AnalyticsEvent {
  return {
    event: 'clickAccoTabsFilter',
    description: 'set filter on price and book',
    element,
    type,
    result: result?.toString().toLowerCase(),
    interaction: true,
  }
}

export interface PricesViewedEventProps {
  transport: string
  boardType: string
  durations: string
  partyComposition: string
  collection: string
  roomType: string
  departureDate: string
  totalGuests: string
  entryPrice: number
  analytics: PriceTableAnalyticsWithBrand
}

export interface ViewPricesEvent {
  event: string
  ecommerce: Ecommerce
  accoId: string
  accoName: string
  accoBrand: string
  accoCategory: string
  accoStars: string
  transport: string
  boardType: string
  duration: string
  rating: string
  partyComposition: string
  totalGuests: string
  cityId: string
  regionId: string
  countryId: string
  city: string
  region: string
  country: string
  entryPrice: number
  promotedPrice: string
  departureDate: string
  originalDate: string
}

interface Ecommerce {
  add: {
    actionField: {
      list: string
    }
    products: Record<string, string>[]
  }
}

export function createPricesViewedEvent({
  transport,
  boardType,
  durations,
  partyComposition,
  totalGuests,
  entryPrice,
  departureDate,
  analytics,
}: PricesViewedEventProps): ViewPricesEvent {
  const { accoId, accoName, accoBrand, breadcrumbs, stars, rating, cityId, regionId, countryId, originList, countryEnglishName, regionEnglishName, cityEnglishName } = analytics
  const daysBeforeDeparture = getDaysBeforeDeparture(departureDate)
  return {
    event: 'viewPrices',
    ecommerce: {
      add: {
        actionField: {
          list: originList ?? '',
        },
        products: [
          {
            id: `${accoId}`,
            name: accoName,
            brand: accoBrand,
            category: breadcrumbs,
            quantity: '0',
            dimension2: stars,
            dimension3: countryId,
            dimension4: transport,
            dimension5: partyComposition,
            dimension6: '', // badges
            dimension7: rating,
            dimension8: durations,
            dimension9: partyComposition,
            dimension10: `${daysBeforeDeparture}`, // days before departure
            dimension11: totalGuests,
            dimension28: '', // roomtype
            dimension38: boardType,
            cityId,
            regionId,
            countryId,
            city: cityEnglishName,
            region: regionEnglishName,
            country: countryEnglishName,
          },
        ],
      },
    },
    accoId: `${accoId}`,
    accoBrand,
    transport,
    boardType,
    duration: durations,
    partyComposition,
    totalGuests,
    rating,
    accoStars: stars,
    accoName,
    accoCategory: breadcrumbs,
    cityId,
    regionId,
    countryId,
    city: cityEnglishName,
    region: regionEnglishName,
    country: countryEnglishName,
    entryPrice,
    promotedPrice: 'shown',
    departureDate: '',
    originalDate: '',
  }
}

interface ViewPricesClickEventProps {
  price: number
  isHighlighted: boolean
}

export function createPricesViewedClickEvent({ price, isHighlighted }: ViewPricesClickEventProps): GA4AnalyticsEvent {
  return {
    event: 'viewPrices',
    description: 'click on price in price table',
    element: 'price cell',
    type: isHighlighted ? 'High price' : 'Low price',
    result: price,
    interaction: true,
  }
}

export interface PricesAdditionalDetailsEventProps {
  transport: string
  boardType: string
  duration: string
  partyComposition: string
  totalGuests: string
  departureDate: string
  entryPrice: string
  campaignId: string
  analytics: PriceTableAnalyticsWithBrand
}

export interface AdditionalDetailsEvent {
  accoId: string
  event: string
  transport: string
  boardType: string
  duration: string
  partyComposition: string
  totalGuests: string
  departureDate: string
  entryPrice: string
  campaignId: string
  promotedPrice: string
  cityId: string
  regionId: string
  countryId: string
  city: string
  region: string
  country: string
}

export function createAdditionalDetailsEvent({
  transport,
  boardType,
  duration,
  partyComposition,
  totalGuests,
  departureDate,
  entryPrice,
  campaignId,
  analytics,
}: PricesAdditionalDetailsEventProps): AdditionalDetailsEvent {
  const { accoId, cityId, regionId, countryId, countryEnglishName, regionEnglishName, cityEnglishName } = analytics
  return {
    event: 'eec.additionalDetail',
    accoId: `${accoId}`,
    transport,
    boardType,
    duration,
    partyComposition,
    totalGuests,
    departureDate,
    entryPrice,
    campaignId,
    promotedPrice: 'shown',
    cityId,
    regionId,
    countryId,
    city: cityEnglishName,
    region: regionEnglishName,
    country: countryEnglishName,
  }
}

export interface FlightEvent {
  event: string
  flightProvider: string
  arrivalAirport: string
  departureAirport: string
  departureDate: string
  departureTime: string
  price: string
  isDefaultOption: string
  type: 'flex' | 'non-flex' | null
}

export interface BusEvent {
  event: string
  arrivalCity: string
  departureCity: string
  departureDate: string
  departureTime: string
  price: string
  isDefaultOption: string
}

export function createFlightSelectedEvent({ route, type }: { route: RouteInfo, type: FlightType }): FlightEvent {
  const flightType = route.flightType === 'flexible' ? 'flex' : 'non-flex'
  return {
    event: type === FlightType.Outbound ? 'outboundFlight' : 'inboundFlight',
    flightProvider: route.airline?.id ?? 'unknown',
    arrivalAirport: route.route?.to?.code,
    departureAirport: route.route?.from?.code,
    departureDate: route.departureDate?.fullDate,
    departureTime: route.departureDate?.time,
    price: route.averagePrice?.toString(),
    isDefaultOption: route.isDefault ? 'yes' : 'no',
    type: flightType,
  }
}

export function createClickedFlightEvent(type: FlightType): GA4AnalyticsEvent {
  return {
    event: 'Flight',
    description: 'Flight component',
    element: 'element_clicked',
    type: type === FlightType.Outbound ? 'outbound' : 'inbound',
    result: 'Select flight',
    interaction: false,
  }
}

interface InteractionEventProps {
  type: string
  result: string
}

export function createFlightComponentInteractionEvent({ type, result }: InteractionEventProps): GA4AnalyticsEvent {
  return {
    event: 'Flight',
    description: 'Flight component',
    element: 'button_group',
    type,
    result,
    interaction: true,
  }
}

export function createBusComponentInteractionEvent({ type, result }: InteractionEventProps): GA4AnalyticsEvent {
  return {
    event: 'Bus',
    description: 'Bus component',
    element: 'button_group',
    type,
    result,
    interaction: true,
  }
}

export function createRoomSelectorInteractionEvent({ type, result }: InteractionEventProps): GA4AnalyticsEvent {
  return {
    event: 'PDP',
    description: 'roomtype selector',
    element: 'Accordion',
    type,
    result,
    interaction: true,
  }
}

export interface ParticipantsAllocatedEventProps {
  transportType: string
  boardType: string
  duration: string
  departureDate: string
  partyComposition: string
  totalGuests: string
  price: number
  roomType: string
  campaignId: string
  analytics: PriceTableAnalyticsWithBrand
}

function getDaysBeforeDeparture(departureDate: string) {
  // TODO Caution! Date.now() is a test enemy, should be replaced as a dependency or provide this value precalculated
  return Math.floor((new Date(departureDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
}

export function createParticipantsAllocatedEvent(props: ParticipantsAllocatedEventProps) {
  const daysBeforeDeparture = getDaysBeforeDeparture(props.departureDate)
  const { accoId, accoName, accoBrand, breadcrumbs, stars, rating, cityId, regionId, countryId, hasSustainabilityCertificate, countryEnglishName, regionEnglishName, cityEnglishName } = props.analytics
  return {
    event: 'eec.add',
    ecommerce: {
      add: {
        actionField: {
          list: 'participants allocated',
        },
        products: [
          {
            name: accoName,
            id: `${accoId}`,
            brand: accoBrand,
            category: breadcrumbs,
            quantity: 1,
            accoStars: stars,
            locationId: cityId,
            rating,
            transport: props.transportType,
            boardType: props.boardType,
            duration: props.duration,
            departureDate: props.departureDate,
            totalGuests: props.totalGuests,
            partyComposition: props.partyComposition,
            daysBeforeDeparture,
            price: Number(props.price.toFixed(2)),
            roomType: props.roomType,
            priceHighlighted: '',
            campaignId: props.campaignId,
            cityId,
            regionId,
            countryId,
            city: cityEnglishName,
            region: regionEnglishName,
            country: countryEnglishName,
            entryPrice: props.price,
            hasSustainabilityCertificate,
          },
        ],
      },
    },
  }
}

export interface AlertServiceAnalyticsEvent extends GA4AnalyticsEvent {
  eventValue: number
}

export interface SubmitEventProps {
  selectedRooms: RoomInfo[]
  analytics: PriceTableAnalyticsWithBrand
  filterSelection: FilterSelection
  selectedPrice?: Price
}

export function createSubmitEvent({ selectedRooms, analytics, filterSelection, selectedPrice }: SubmitEventProps) {
  const {
    accoId: id,
    accoName: name,
    accoBrand: brand,
    breadcrumbs: category,
    stars,
    rating: accoRating,
    cityId: accoCityId,
    regionId: accoRegionId,
    countryId: accoCountryId,
    originList,
    hasSustainabilityCertificate,
    countryEnglishName,
    regionEnglishName,
    cityEnglishName,
  } = analytics
  const { TransportType: transportType, Participants } = filterSelection
  const {
    mealplan: boardType = '',
    duration = '',
    departureDate = '',
    averagePrice: price = 0,
  } = selectedPrice || {}
  const { distribution: partyComposition, total: totalGuests } = formatParticipantDistributionToGA4Result(
    Participants || [],
  )
  const daysBeforeDeparture = getDaysBeforeDeparture(departureDate)
  const roomType = selectedRooms.map(({ name: roomName }) => roomName).join(' - ')

  return {
    event: 'eec.submit',
    ecommerce: {
      add: {
        actionField: {
          list: originList,
        },
        products: [
          {
            id,
            name,
            brand,
            category,
            quantity: ' 1',
            price,
            dimension2: stars,
            dimension3: accoCountryId,
            dimension4: transportType,
            dimension7: accoRating,
            dimension5: partyComposition,
            dimension8: duration.toString(),
            dimension9: partyComposition,
            dimension10: daysBeforeDeparture,
            dimension11: totalGuests,
            dimension13: departureDate,
            dimension28: roomType,
            dimension38: boardType,
            dimension47: departureDate,
            cityId: accoCityId,
            regionId: accoRegionId,
            countryId: accoCountryId,
            city: cityEnglishName,
            region: regionEnglishName,
            country: countryEnglishName,
            hasSustainabilityCertificate,
          },
        ],
      },
    },
    accoId: id,
    accoName: name,
    accoBrand: brand,
    accoCategory: category,
    accoStars: stars,
    transport: transportType,
    boardType,
    duration: duration.toString(),
    rating: accoRating,
    partyComposition,
    totalGuests,
    departureDate,
    originalDate: departureDate,
    entryPrice: price,
    promotedPrice: 'shown',
    cityId: accoCityId,
    regionId: accoRegionId,
    countryId: accoCountryId,
    city: cityEnglishName,
    region: regionEnglishName,
    country: countryEnglishName,
  }
}

const deepLinkingEventCommonProps = {
  event: 'CRO',
  description: 'ID 425 | Surface deeplinking functionality',
  element: 'NL', // TODO if the AB is successful change element dynamically based on the site we are on,
  interaction: true,
}

export function createDeepLinkingSharingModalOpenedEvent() {
  return {
    ...deepLinkingEventCommonProps,
    type: 'click',
    result: 'open',
  }
}

export function createDeepLinkingCopiedSuccessfullyEvent(result: boolean) {
  return {
    ...deepLinkingEventCommonProps,
    type: 'sharesuccess',
    result,
  }
}

export function createBusSelectedEvent({ bus, isDefault, type }: { bus: BusInfo, isDefault: boolean, type: BusType }): BusEvent {
  return {
    event: type === BusType.Outbound ? 'outboundBus' : 'inboundBus',
    arrivalCity: bus.arrivalCityName,
    departureCity: bus.departureCityName,
    departureDate: bus.departureDate?.fullDate,
    departureTime: bus.departureDate?.time,
    price: bus.averagePrice?.toString(),
    isDefaultOption: isDefault ? 'yes' : 'no',
  }
}
